import { useState, useRef, useEffect, useMemo } from "react";

interface Props {
  options: string[];             // supported insurer names
  value: string;                 // current pick: a supported name | a new-insurer name | ""
  onChange: (v: string) => void; // commits the chosen name
  // Optional per-name distinctive aliases (trade names / abbreviations). When present,
  // the shortlist also matches a name whose alias contains the query — so "union
  // nationale" or "nia" surfaces the right insurer even though the display name lacks
  // those words. Absent/empty => name-only substring match (byte-identical to before).
  aliasesByName?: Record<string, string[]>;
  // Names belonging to the UNVERIFIED testing tier; rendered with a "testing" tag so a
  // work-in-progress insurer is never mistaken for a finance-verified one. Absent =>
  // no tags (byte-identical to before).
  testingNames?: Set<string>;
}

// Collapse to lowercase alphanumerics so hyphens/spaces/punctuation don't block a match
// ("al-ittihad" vs "al ittihad" vs "alittihad" all normalise equal).
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

// A searchable insurer picker replacing the plain <select>: a text field that both shows
// the list and lets the user type to shortlist it letter by letter. Selecting a matched
// row commits that supported insurer; if the typed text matches nothing, a "Use new
// insurer" row commits the raw text as a NEW insurer (the parent detects its layout on
// first run). UI only — the committed value is the insurer name either way.
export default function InsurerCombobox({ options, value, onChange, aliasesByName, testingNames }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // While open, the input shows what the user is typing so the list narrows; while closed
  // it mirrors the committed pick.
  const shown = open ? query : value;

  const q = query.trim();
  const matches = useMemo(() => {
    const needle = norm(q);
    if (!needle) return options;
    return options.filter((n) => {
      if (norm(n).includes(needle)) return true;
      // Also match on the insurer's distinctive aliases, so a trade name / abbreviation
      // the display name doesn't contain still shortlists it (e.g. "union nationale",
      // "nia"). Alias match is either direction: the query is a substring of an alias,
      // or an alias is a substring of the query (a longer typed name incl. the alias).
      const aliases = aliasesByName?.[n] ?? [];
      return aliases.some((a) => {
        const na = norm(a);
        return na && (na.includes(needle) || needle.includes(na));
      });
    });
  }, [options, q, aliasesByName]);

  // Offer "Use new insurer" only when the user has typed a name that isn't an exact
  // (case-insensitive) supported insurer — that's the new-insurer path.
  const exact = options.some((n) => n.toLowerCase() === q.toLowerCase());
  const newRow = q && !exact ? q : null;
  const rows = useMemo(() => matches.map((n) => ({ name: n, isNew: false })), [matches]);
  const allRows = newRow ? [...rows, { name: newRow, isNew: true }] : rows;

  useEffect(() => { setActive(0); }, [query, open]);

  const commit = (name: string) => {
    onChange(name);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, allRows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (allRows[active]) commit(allRows[active].name); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input
        className="input"
        role="combobox"
        aria-expanded={open}
        placeholder="Select or type to search…"
        value={shown}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && (
        <div style={{ position: "absolute", zIndex: 10, background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", width: "100%", maxHeight: 260, overflowY: "auto", boxShadow: "var(--shadow-card)" }}>
          {allRows.length === 0 && (
            <div className="muted" style={{ padding: "6px 10px", fontSize: "0.82rem" }}>No insurers.</div>
          )}
          {allRows.map((r, i) => {
            const selected = !r.isNew && r.name === value;
            return (
              <div key={r.isNew ? `__new__${r.name}` : r.name}
                onMouseDown={(e) => { e.preventDefault(); commit(r.name); }} onMouseEnter={() => setActive(i)}
                style={{
                  padding: "6px 10px", cursor: "pointer", fontSize: "0.85rem",
                  fontStyle: r.isNew ? "italic" : "normal",
                  borderTop: r.isNew && rows.length ? "1px solid var(--border)" : "none",
                  background: i === active ? "rgba(33,125,163,0.10)" : selected ? "rgba(33,125,163,0.06)" : "transparent",
                }}>
                {r.isNew ? <>Use new insurer: “{r.name}”</> : (
                  <>
                    {r.name}
                    {testingNames?.has(r.name) && (
                      <span className="muted" style={{ fontSize: "0.75rem", marginLeft: 6 }}
                        title="Under testing — not finance-verified">· testing</span>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
